import axios from 'axios';

// Get backend URL from environment variable
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Get token from the same keys used by AuthContext
    const token = localStorage.getItem('brixbot_token') || sessionStorage.getItem('brixbot_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if this is a login attempt (to allow error messages to display)
      const isLoginAttempt = error.config?.url?.includes('/auth/login');
      
      if (!isLoginAttempt) {
        // Clear tokens using the same keys as AuthContext and redirect to login on unauthorized
        localStorage.removeItem('brixbot_token');
        localStorage.removeItem('brixbot_user');
        sessionStorage.removeItem('brixbot_token');
        sessionStorage.removeItem('brixbot_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Utility functions
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

export const getStatusColor = (status) => {
  const statusColors = {
    'pending': 'text-yellow-400',
    'in_progress': 'text-blue-400',
    'testing': 'text-purple-400',
    'final_payment': 'text-orange-400',
    'deploy': 'text-green-400',
    'done': 'text-green-600',
    'completed': 'text-green-600',
    'deployed': 'text-green-400',
    'active': 'text-green-400',
    'inactive': 'text-gray-400',
    'cancelled': 'text-red-400',
    'failed': 'text-red-400'
  };
  
  return statusColors[status] || 'text-gray-400';
};

// Error handler utility
export const handleAPIError = (error, defaultMessage = 'An error occurred') => {
  console.error('API Error:', error);
  
  if (error.response?.data?.detail) {
    if (Array.isArray(error.response.data.detail)) {
      // Handle validation errors
      return error.response.data.detail.map(err => err.msg).join(', ');
    }
    return error.response.data.detail;
  }
  
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error.message) {
    return error.message;
  }
  
  return defaultMessage;
};

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  verifyEmail: (email, code) => api.post('/auth/verify-email', { email, code }),
  resendVerification: (email) => api.post('/auth/request-email-verification', { email }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (email, code, newPassword) => 
    api.post('/auth/reset-password', { email, code, new_password: newPassword }),
  changePassword: (currentPassword, newPassword) => 
    api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
  getUsers: () => api.get('/auth/users'),
  getCurrentUser: () => api.get('/auth/me'),
  getEmployees: () => api.get('/auth/employees'),
  createEmployee: (employeeData) => api.post('/auth/create-employee', employeeData),
  updateEmployee: (userId, profileData) => api.put(`/auth/users/${userId}/profile`, profileData),
  deleteEmployee: (userId) => api.delete(`/auth/users/${userId}`),
};

// Chatbot API
export const chatbotAPI = {
  createChatbot: (chatbotData) => api.post('/chatbots', chatbotData),
  getChatbots: () => api.get('/chatbots'),
  getChatbot: (id) => api.get(`/chatbots/${id}`),
  updateChatbotStatus: (id, status) => api.put(`/chatbots/${id}/status`, { status }),
  updateChatbotToTesting: (id, testingData) => api.put(`/chatbots/${id}/testing`, testingData),
  updateChatbotToFinalPayment: (id) => api.put(`/chatbots/${id}/final-payment`),
  selectPlan: (id, planData) => api.post(`/chatbots/${id}/select-plan`, planData),
  deleteChatbot: (id) => api.delete(`/chatbots/${id}`),
  reassignChatbot: (id, employeeId) => api.put(`/chatbots/${id}/reassign`, null, { params: { new_employee_id: employeeId } }),
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/chatbots/${id}/upload-logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadLogo: (id) => api.get(`/chatbots/${id}/logo/download`, { responseType: 'blob' }),
  downloadFile: (chatbotId, fileId) => api.get(`/chatbots/${chatbotId}/files/${fileId}/download`, { responseType: 'blob' }),
  checkCustomerHasChatbot: (customerId) => api.get(`/chatbots/${customerId}/has-chatbot`),
  // Dashboard endpoints
  getDashboard: (id) => api.get(`/chatbots/${id}/dashboard`),
  updateDashboard: (id, dashboardData) => api.put(`/chatbots/${id}/dashboard`, dashboardData),
  // Chat history endpoints
  uploadChatHistory: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/chatbots/${id}/chat-history/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadChatHistory: (chatbotId, filename) => api.get(`/chatbots/${chatbotId}/chat-history/${filename}/download`, { responseType: 'blob' }),
  deleteChatHistory: (chatbotId, filename) => api.delete(`/chatbots/${chatbotId}/chat-history/${filename}`),
};

// Chat API
export const chatAPI = {
  getRooms: (filterType = 'all') => api.get('/chat/rooms', { params: { filter_type: filterType } }),
  getRoom: (id) => api.get(`/chat/rooms/${id}`),
  getMessages: (roomId) => api.get(`/chat/rooms/${roomId}/messages`),
  sendMessage: (roomId, messageData) => api.post(`/chat/rooms/${roomId}/messages`, messageData),
  markAsRead: (roomId) => api.put(`/chat/rooms/${roomId}/mark-read`),
  markMessagesRead: (roomId) => api.post(`/chat/rooms/${roomId}/mark-read`),
  createRoom: (roomData) => api.post('/chat/rooms', roomData),
  deleteRoom: (roomId) => api.delete(`/chat/rooms/${roomId}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getAllUsersData: () => api.get('/dashboard/all-users-data'),
  getMyChatbots: () => api.get('/dashboard/my-chatbots'),
  // Keep both names for backward compatibility
  assignChatbot: (assignmentData) => api.post('/dashboard/assignments', assignmentData),
  createAssignment: (assignmentData) => api.post('/dashboard/assignments', assignmentData),
  deleteUserByAdmin: (userId) => api.delete(`/dashboard/users/${userId}`),
  getRecentActivity: (limit) => api.get(`/dashboard/recent-activity`, { params: { limit } }),
};

// Payment API
export const paymentAPI = {
  // Aliases used by components: createChatbotCreationSession
  createChatbotCreationSession: (companyData) => 
    api.post('/payments/chatbot-creation/checkout-session', companyData),
  createChatbotCreationCheckoutSession: (companyData) => 
    api.post('/payments/chatbot-creation/checkout-session', companyData),
  createFinalPaymentCheckoutSession: (chatbotId) => 
    api.post(`/payments/final-payment/checkout-session/${chatbotId}`),
  createPlanSelectionCheckoutSession: (chatbotId, planId) => 
    api.post(`/payments/plan-selection/checkout-session/${chatbotId}`, null, { params: { plan_id: planId } }),
  // Endpoint returns global final payment status; no chatbot ID param required
  getFinalPaymentStatus: () => 
    api.get(`/payments/final-payment/status`),
  // Used by PaymentModal - include origin_url and chatbot_id
  createDepositCheckoutSession: (chatbotId) => 
    api.post('/payments/deposit/checkout-session', { chatbot_id: chatbotId, origin_url: window.location.origin }),
  createUserDepositCheckoutSession: (originUrl) => 
    api.post('/payments/user-deposit/checkout-session', { origin_url: originUrl }),
  createBillingPortalSession: () => 
    api.post('/payments/create-billing-portal-session'),
  getBillingStatus: () =>
    api.get('/payments/billing-status'),
  // Check generic payment session status
  getPaymentStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  completeChatbotCreationAfterPayment: (userEmailOrData) =>
    // maintain compatibility: if an object passed, treat as body; otherwise treat as legacy endpoint
    typeof userEmailOrData === 'string'
      ? api.post(`/payments/chatbot-creation/complete/${userEmailOrData}`)
      : api.post(`/payments/chatbot-creation/complete`, userEmailOrData),
  manualCompleteChatbotCreation: (userEmail) =>
    api.post(`/payments/chatbot-creation/manual-complete/${userEmail}`),
  getDebugUserPayments: (userEmail) =>
    api.get(`/payments/debug/user-payments/${userEmail}`),
};

// Settings API
export const settingsAPI = {
  getProfile: () => api.get('/settings/profile'),
  updateProfile: (profileData) => api.put('/settings/profile', profileData),
  revertEmailChange: () => api.post('/settings/revert-email'),
  verifyEmailChange: (email, code) => api.post('/settings/verify-email-change', { email, code }),
  resendEmailChangeCode: () => api.post('/settings/resend-email-change-code'),
  getBillingInfo: () => api.get('/settings/billing'),
  changePlan: (planData) => api.post('/settings/change-plan', planData),
  getPlanChangeStatus: (sessionId) => api.get(`/settings/plan-change-status/${sessionId}`),
  deleteAccount: (deletionData) => api.post('/settings/delete-account', deletionData),
  restoreAccount: (restoreData) => api.post('/settings/restore-account', restoreData),
  cleanupCustomers: () => api.delete('/settings/admin/cleanup-customers'),
  cleanupCustomersExcept: (email) => api.delete(`/settings/admin/cleanup-customers-except`, { params: { preserve_email: email } }),
  manuallyVerifyUser: (email) => api.post(`/settings/admin/verify-user/${email}`),
};

// Presence API
export const presenceAPI = {
  updatePresence: (status) => api.post('/presence/update', { status }),
  getPresence: (userId) => api.get(`/presence/${userId}`),
  getOnlineUsers: () => api.get('/presence/online'),
};

export default api;